import { useEffect, useState } from 'react';

export interface MonitoringReadState<T> {
  readonly status: 'LOADING' | 'READY' | 'ERROR';
  readonly data: T | null;
  readonly lastReadAt: number | null;
  readonly error: string | null;
}
export function initialReadState<T>(): MonitoringReadState<T> {
  return { status: 'LOADING', data: null, lastReadAt: null, error: null };
}
export const POLL_INTERVAL_MS = 1000;
/** No overlapping reads. Stop aborts the in-flight request and discards late results. */
export function startMonitoringPolling<T>(
  read: (signal: AbortSignal) => Promise<T>,
  publish: (state: MonitoringReadState<T>) => void,
  schedule: (callback: () => void, ms: number) => () => void = (callback, ms) => {
    const timer = setInterval(callback, ms);
    return () => clearInterval(timer);
  },
  now: () => number = Date.now,
): () => void {
  let stopped = false;
  let inFlight = false;
  let state = initialReadState<T>();
  let controller: AbortController | null = null;
  const poll = async () => {
    if (stopped || inFlight) return;
    inFlight = true;
    controller = new AbortController();
    try {
      const data = await read(controller.signal);
      if (!stopped) {
        state = { status: 'READY', data, lastReadAt: now(), error: null };
        publish(state);
      }
    } catch {
      if (!stopped) {
        state = { ...state, status: 'ERROR', error: '本地请求失败或响应无效；等待下次自动读取。' };
        publish(state);
      }
    } finally {
      inFlight = false;
    }
  };
  const cancel = schedule(() => {
    void poll();
  }, POLL_INTERVAL_MS);
  void poll();
  return () => {
    stopped = true;
    cancel();
    controller?.abort();
  };
}

export function useMonitoringRead<T>(key: string, read: (signal: AbortSignal) => Promise<T>) {
  const [retry, setRetry] = useState(0);
  const [result, setResult] = useState({ key, state: initialReadState<T>() });
  useEffect(() => {
    setResult({ key, state: initialReadState<T>() });
    return startMonitoringPolling(read, (state) => setResult({ key, state }));
  }, [key, read, retry]);
  return {
    state: result.key === key ? result.state : initialReadState<T>(),
    retry: () => setRetry((value) => value + 1),
  };
}
